import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { statisticsPeriodOptions, visibleDateRange } from './statistics'
import { type PinduoduoCustomRange, type PinduoduoStatisticsPeriod, type PinduoduoStatisticsWindow } from './pinduoduoStatisticsWindow'
export { pinduoduoStatisticsSearch, pinduoduoStatisticsWindow } from './pinduoduoStatisticsWindow'
export type { PinduoduoCustomRange, PinduoduoStatisticsPeriod, PinduoduoStatisticsWindow } from './pinduoduoStatisticsWindow'

export const pinduoduoStatisticsPeriodOptions: Array<{ key: PinduoduoStatisticsPeriod, label: string }> = [
  ...statisticsPeriodOptions,
  { key: 'previous2Month', label: '上上月' },
  { key: 'custom', label: '自定义' },
]
const supportedPeriods = new Set<PinduoduoStatisticsPeriod>(pinduoduoStatisticsPeriodOptions.map((item) => item.key))

export function usePinduoduoStatisticsPeriod(defaultPeriod: PinduoduoStatisticsPeriod = 'last30') {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('period') as PinduoduoStatisticsPeriod | null
  const period = requested && supportedPeriods.has(requested) ? requested : defaultPeriod
  const customRange = {
    startDate: searchParams.get('start') || '',
    endDate: searchParams.get('end') || '',
  }
  const setPeriod = useCallback((value: PinduoduoStatisticsPeriod) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('period', value)
      return next
    }, { replace: true })
  }, [setSearchParams])
  const setCustomRange = useCallback((value: PinduoduoCustomRange) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('period', 'custom')
      next.set('start', value.startDate)
      next.set('end', value.endDate)
      return next
    }, { replace: true })
  }, [setSearchParams])
  return [period, setPeriod, customRange, setCustomRange] as const
}

export function PinduoduoStatisticsBar({
  period,
  onChange,
  window,
  customRange,
  onCustomRangeChange,
  title = '订单与利润统计范围',
  ariaLabel = '拼多多订单与利润统计范围',
  customAriaLabel = '自定义订单与利润日期',
}: {
  period: PinduoduoStatisticsPeriod
  onChange: (value: PinduoduoStatisticsPeriod) => void
  window: PinduoduoStatisticsWindow | null
  customRange: PinduoduoCustomRange
  onCustomRangeChange: (value: PinduoduoCustomRange) => void
  title?: string
  ariaLabel?: string
  customAriaLabel?: string
}) {
  const actualRange = window ? visibleDateRange(window.dates) : '订单数据加载中'
  const detail = !window
    ? actualRange
    : window.complete
      ? `${window.label} · ${actualRange} · 完整${window.requestedDayCount}天`
      : `${window.label} · 请求 ${window.startDate} 至 ${window.endDate} · 仅覆盖 ${actualRange}（${window.dates.length}/${window.requestedDayCount}天）`
  return <div className={`pdd-statistics-bar ${window && !window.complete ? 'is-partial' : ''}`}>
    <div><strong>{title}</strong><small>{detail}</small></div>
    <div className="pdd-statistics-controls">
      <div className="metric-switch" role="group" aria-label={ariaLabel}>
        {pinduoduoStatisticsPeriodOptions.map((item) => <button
          type="button"
          key={item.key}
          className={item.key === period ? 'is-active' : ''}
          aria-pressed={item.key === period}
          onClick={() => item.key === 'custom' && window
            ? onCustomRangeChange({
              startDate: customRange.startDate || window.dataStartDate,
              endDate: customRange.endDate || window.dataEndDate,
            })
            : onChange(item.key)}
        >{item.label}</button>)}
      </div>
      {period === 'custom' && window ? <div className="pdd-custom-range" role="group" aria-label={customAriaLabel}>
        <label>开始<input type="date" min={window.dataStartDate} max={window.dataEndDate} value={customRange.startDate || window.startDate} onChange={(event) => {
          const startDate = event.target.value
          const currentEnd = customRange.endDate || window.endDate
          onCustomRangeChange({ startDate, endDate: startDate > currentEnd ? startDate : currentEnd })
        }} /></label>
        <span>至</span>
        <label>结束<input type="date" min={window.dataStartDate} max={window.dataEndDate} value={customRange.endDate || window.endDate} onChange={(event) => {
          const endDate = event.target.value
          const currentStart = customRange.startDate || window.startDate
          onCustomRangeChange({ startDate: endDate < currentStart ? endDate : currentStart, endDate })
        }} /></label>
      </div> : null}
    </div>
  </div>
}
